import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import PropTypes from 'prop-types'
import React from 'react'
import { Provider } from 'react-redux'
import { browserHistory, Router } from 'react-router'

// SureCo's Antd react component library
// @surecompanies/core_components = antd v5 lib + any custom components
// @surecompanies/theme = theme values pulled from @hixme-ui/theme and set as defaults for antd v5 ConfigProvider
import { DesignSystemProvider } from '@surecompanies/react-design-system'
import { ThemeProvider as SureCoAntDThemeProvider } from '@surecompanies/theme'
import 'antd/dist/reset.css'

import theme, { ThemeProvider } from '@hixme-ui/theme'
import { clearModal } from '@hixme/modal'
import { ToastContainer } from 'react-toastify'
import { injectStyle } from 'react-toastify/dist/inject-style'

import modal from 'modules/app-core/modal'

import { defaultFontFamily, TextColors } from '@sureco/design-system'
import { ErrorBoundary } from 'components/ErrorBoundary'
import { NOT_FOUND_PATH } from 'constants/route'
import tryScroll from 'helpers/tryScroll'
import { initThirdPartyTrackersIfAllowed } from 'helpers/initThirdPartyTrackers'
import { withLDProvider } from 'launchdarkly-react-client-sdk'
import { queryClient } from 'lib/queryClient'
import { navigationProgress } from 'enrollment-platform/rbac/navigation-progress'
import { NavigationProgressBar } from 'enrollment-platform/rbac/NavigationProgressBar'
import appConfig from '../modules/app-config'

const handleRouteError = () => (error) => {
  // A transition settled (with an error) — clear any in-flight nav progress.
  navigationProgress.reset()
  if (parseInt(error.status, 10) === 401) {
    browserHistory.push(NOT_FOUND_PATH)
  }
}

const EXCLUDE_SCROLL_PATH_REGEXES = [
  /^\/plans-v2\/[^/]+\/review/,
  // QLE detail tabs write `?qle=` without changing the path; skip scroll-to-top.
  /^\/qle\/[^/]+/,
]

function onUpdate(store) {
  return () => {
    // Transition completed — clear the nav progress bar.
    navigationProgress.reset()
    initThirdPartyTrackersIfAllowed()
    store.dispatch(modal.actions.hideModal())
    store.dispatch(clearModal())
    // clearModal leaves `overflow: unset` inline on body, which outranks the
    // antd dialog and drawer scroll lock (see styles/core.scss).
    document.body.style.removeProperty('overflow')
    const { pathname } = browserHistory.getCurrentLocation()
    if (!EXCLUDE_SCROLL_PATH_REGEXES.some((re) => re.test(pathname))) {
      tryScroll()
    }
  }
}

// Re-exported for existing importers; the singleton now lives in lib/queryClient
// so non-React code (route guards, logout) can import it without an app-tree cycle.
export { queryClient }

const AppContainer = ({ routes, store }) => (
  <Provider store={store}>
    <DesignSystemProvider>
      <SureCoAntDThemeProvider
        theme={{
          token: {
            borderRadiusLG: 4,
            borderRadiusSM: 4,
            borderRadiusXS: 4,
            fontFamily: defaultFontFamily,
            colorPrimary: TextColors.primary,
            colorError: TextColors.error,
            Table: {
              borderColor: TextColors.tertiary,
            },
          },
        }}
      >
        <QueryClientProvider client={queryClient}>
          <ThemeProvider
            theme={{
              ...theme,
              fontFamily: {
                thinnest: defaultFontFamily,
                thinner: defaultFontFamily,
                thin: defaultFontFamily,
                normal: defaultFontFamily,
                bold: defaultFontFamily,
                bolder: defaultFontFamily,
                boldest: defaultFontFamily,
              },
            }}
          >
            <div style={{ height: '100%' }}>
              <NavigationProgressBar />
              <ToastContainer theme="colored" position="bottom-right" />
              {injectStyle()}
              <ErrorBoundary>
                <Router
                  history={browserHistory}
                  onError={handleRouteError(store)}
                  onUpdate={onUpdate(store)}
                >
                  {routes}
                </Router>
              </ErrorBoundary>
            </div>
          </ThemeProvider>
          <ReactQueryDevtools buttonPosition="bottom-left" />
        </QueryClientProvider>
      </SureCoAntDThemeProvider>
    </DesignSystemProvider>
  </Provider>
)

AppContainer.propTypes = {
  routes: PropTypes.object.isRequired,
  store: PropTypes.object.isRequired,
}

// Update the export default to use your environment-specific client ID and a sample context:
export default withLDProvider({
  clientSideID: appConfig.getLaunchDarklyConfig().clientSideID,
  reactOptions: {
    useCamelCaseFlagKeys: false,
  },
})(AppContainer)
